import { ArrowLeft, ArrowUpRight, MapPin, ShieldCheck, Trophy } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { toast } from "../components/ui/sonner";
import { franchiseById } from "../data/riotballTeams";
import { alignFranchise, fetchMe, formatApiError, getCachedUser } from "../utils/auth";

/* Franchise dossier — the league's sealed file on one team:
   identity, active roster, season record, and emblem history. */
export default function TeamDossier() {
  const { teamId } = useParams();
  const team = franchiseById(teamId);
  const [user, setUser] = useState(() => getCachedUser());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchMe().then(setUser).catch(() => {});
    const onAuth = (e) => setUser(e.detail?.user ?? null);
    window.addEventListener("dolo-auth", onAuth);
    return () => window.removeEventListener("dolo-auth", onAuth);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [teamId]);

  if (!team) {
    return (
      <main className="dossier-page" data-testid="team-dossier-missing">
        <Link to="/riotball" className="back-link" data-testid="dossier-back-link">
          <ArrowLeft size={16} /> BACK TO AL RIOTBALL
        </Link>
        <section className="dossier-hero">
          <span className="eyebrow">LEAGUE ARCHIVE // FILE NOT FOUND</span>
          <h1>No such franchise.</h1>
          <p>The registry holds exactly ten teams on the 2355 tour. This file was burned, or never existed.</p>
        </section>
        <Footer />
      </main>
    );
  }

  const aligned = user?.franchise === team.id;

  const align = async () => {
    if (!user) {
      toast.error("SIGN IN TO ALIGN — your Titan Tag needs a vault account.");
      return;
    }
    if (saving || aligned) return;
    setSaving(true);
    try {
      await alignFranchise(team.id);
      toast.success(`ALIGNED — ${team.name}. Your runner roster row now carries the crest.`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "ALIGNMENT FAILED — RETRY."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="dossier-page" data-testid={`team-dossier-${team.id}`} style={{ "--team-color": team.color }}>
      <Link to="/riotball" className="back-link" data-testid="dossier-back-link">
        <ArrowLeft size={16} /> BACK TO AL RIOTBALL
      </Link>

      <section className="dossier-hero">
        <Reveal className="dossier-hero-copy" immediate>
          <span className="eyebrow">LEAGUE ARCHIVE // FRANCHISE DOSSIER — EST. {team.founded}</span>
          <h1 data-testid="dossier-team-name">{team.name}</h1>
          <p className="dossier-tag">{team.tag}</p>
          <p>{team.lore}</p>
          <div className="dossier-facts">
            <span data-testid="dossier-record">
              <Trophy size={13} /> {team.record}
            </span>
            <span data-testid="dossier-home">
              <MapPin size={13} /> {team.home}
            </span>
          </div>
          <button
            type="button"
            className={`dossier-align-btn${aligned ? " dossier-align-active" : ""}`}
            onClick={align}
            disabled={saving || aligned}
            data-testid="dossier-align-button"
          >
            <ShieldCheck size={15} />
            {aligned ? "YOUR FRANCHISE — ALIGNED" : saving ? "ALIGNING…" : "ALIGN YOUR TITAN TAG"}
          </button>
        </Reveal>
        <Reveal className="dossier-hero-emblem" delay={0.08}>
          <img src={`/assets/team-logo-${team.id}.png`} alt={team.alt} data-testid="dossier-emblem" />
          <span>CURRENT CREST // OFFICIAL TEMPLATE</span>
        </Reveal>
      </section>

      <section className="dossier-section" data-testid="dossier-roster">
        <span className="eyebrow">PERSONNEL // ACTIVE ROSTER — 2355</span>
        <h2>Certified athletes.</h2>
        <div className="dossier-roster-table">
          <div className="dossier-roster-head" aria-hidden="true">
            <span>UNIT</span>
            <span>CALLSIGN</span>
            <span>POSITION</span>
            <span>LEAGUE FILE</span>
          </div>
          {team.players.map((p) => (
            <div className="dossier-roster-row" key={p.unit} data-testid={`dossier-player-${p.unit.replace(/\s+/g, "-").toLowerCase()}`}>
              <strong>{p.unit}</strong>
              <span>{p.name}</span>
              <em>{p.position}</em>
              <p>{p.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="dossier-section" data-testid="dossier-seasons">
        <span className="eyebrow">OFFICIAL RECORD // SEASON LEDGER</span>
        <h2>The record, as kept.</h2>
        <div className="dossier-seasons">
          {team.seasons.map((s) => (
            <Reveal className="dossier-season" key={s.year} data-testid={`dossier-season-${s.year}`}>
              <strong>{s.year}</strong>
              <span>{s.result}</span>
              <p>{s.note}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="dossier-section" data-testid="dossier-emblem-history">
        <span className="eyebrow">EMBLEM HISTORY // ARCHIVE PLATES</span>
        <h2>Every mark the franchise has flown.</h2>
        <div className="dossier-emblems">
          {team.emblemHistory.map((era, i) => (
            <Reveal className="dossier-era" delay={i * 0.06} key={era.era} data-testid={`dossier-era-${i + 1}`}>
              {era.image ? (
                <img src={era.image} alt={`${team.name} ${era.era} emblem`} loading="lazy" />
              ) : (
                <div className="dossier-era-lost" aria-hidden="true">
                  <span>PLATE SEALED</span>
                </div>
              )}
              <strong>{era.era}</strong>
              <span className="dossier-era-year">{era.year}</span>
              <p>{era.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="dossier-foot">
        <Link to="/riotball" className="dossier-tour-link" data-testid="dossier-tour-link">
          <ArrowUpRight size={15} /> SCOUT THE FULL TOUR — TEN FRANCHISES
        </Link>
      </div>

      <Footer />
    </main>
  );
}
